/**
 * Image-frame creation helper for the desktop input bar.
 *
 * Shared by text, paste, drag-drop, and dictation entry points.
 *
 * Reads `currentProject` / `currentSpace` from the Zustand stores at call
 * time, then creates one image frame per upload. The workspace view places
 * them where Tidy puts the newest frames on the old canvas; the wall leaves
 * placement to the backend.
 */
import { translate } from "@/i18n";
import { desktopShellMessages } from "@/i18n/messages/desktop-shell";
import { ingestFrame } from "@/lib/frame-ingest";
import { imageFrameSize, imagePixelSize } from "@/lib/image-frame-size";
import { workspaceViewAdapter } from "@/lib/workspace-view-adapter";
import { useNotificationStore } from "@/stores/notification-store";
import { useProjectStore } from "@/stores/project-store";

export interface UploadedImage {
  id: string;
  url: string;
  width?: number;
  height?: number;
  filename?: string;
}

export async function createImageFramesForUploads(
  uploads: UploadedImage[],
): Promise<void> {
  if (uploads.length === 0) return;
  const pid = useProjectStore.getState().currentProject?.id;
  const sid = useProjectStore.getState().currentSpace?.id;
  if (!pid || !sid) return;
  const { createFrame } = await import("@/api/spaces");
  const sizes = uploads.map(imageFrameSize);
  const positions = workspaceViewAdapter().framePositions({
    kind: "tidy",
    frameType: "image",
    sizes,
  });
  for (const [index, img] of uploads.entries()) {
    const position = positions?.[index];
    try {
      const frame = await createFrame(pid, sid, {
        frame_type: "image",
        title: img.filename || translate(desktopShellMessages, "pastedImage"),
        content: {
          images: [
            {
              url: img.url,
              alt:
                img.filename || translate(desktopShellMessages, "pastedImage"),
              ...imagePixelSize(img),
            },
          ],
        },
        ...(position ? { position_x: position.x, position_y: position.y } : {}),
        width: sizes[index]!.width,
        height: sizes[index]!.height,
      });
      ingestFrame(frame);
    } catch (e) {
      console.error("[image-frame] Failed to create image frame:", e);
      useNotificationStore.getState().addSystemToast({
        id: `image-frame-fail-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        level: "error",
        title: translate(desktopShellMessages, "imageAddFailed"),
        description:
          e instanceof Error
            ? e.message
            : translate(desktopShellMessages, "unknownError"),
      });
    }
  }
}
